import {
	Component, ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Optional,
	Output,
	SimpleChanges,
	TemplateRef
} from '@angular/core';
import {
	AiConversationConfig,
	AiContextTag,
	AiModelRef,
	AiMessage,
	AiCopySettings,
	AiMediaSettings,
	AiBrandKitSettings,
	VideoReferenceType
} from '../models';
import { AI_ADAPTER, AiAdapter } from '../adapter/adapter';
import { MatDialog } from "@angular/material/dialog";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-ai-conversation-shell',
	templateUrl: './ai-conversation-shell.component.html',
	styleUrls: ['./ai-conversation-shell.component.scss']
})
export class AiConversationShellComponent implements OnInit, OnChanges, OnDestroy {
	readonly DEFAULT_COPY_LOADING_TEXT = $localize`:Loading Text@@aiConversationShellCopyLoadingText:Thinking...`;
	readonly DEFAULT_IMAGE_LOADING_TEXT = $localize`:Loading Text@@aiConversationShellImageLoadingText:Generating images, this may take a minute.`;
	readonly DEFAULT_VIDEO_LOADING_TEXT = $localize`:Loading Text@@aiConversationShellVideoLoadingText:Generating video, this may take several minutes.`;
	readonly TRANSLATION_CONTEXT_TAG_SINGULAR = $localize`:Context Tag@@aiConversationShellContextTagSingular:context`;
	readonly TRANSLATION_CONTEXT_TAG_PLURAL = $localize`:Context Tag@@aiConversationShellContextTagPlural:contexts`;

	readonly INIT_LOCAL_MESSAGES: AiMessage[] = [
		{ id: 'm0', role: 'assistant', type: 'text', content: $localize`:Welcome assistant message@@aiConversationShellWelcomeAssistantMessage:Welcome! Try a suggestion below or type your own prompt.` }
	]

	@Input() config: AiConversationConfig;
	@Input() messages: AiMessage[] = [];
	@Input() useAdapter: boolean = true;
	@Input() addedIds: Set<string> | string[] = [];
	@Input() isLoading = false;

	@Output() closed = new EventEmitter<void>();
	@Output() openSettings = new EventEmitter<void>();
	@Output() openBrandKit = new EventEmitter<void>();
	@Output() modelChanged = new EventEmitter<AiModelRef>();
	@Output() sendMessage = new EventEmitter<{prompt: string, config: AiConversationConfig}>();
	@Output() resultAction = new EventEmitter<import('../models').AiResultAction>();
	@Output() addReferenceImages = new EventEmitter<VideoReferenceType | undefined>();
	@Output() removeReferenceImage = new EventEmitter<number>();

	prompt: string = '';
	currentModel?: AiModelRef;
	availableModels: AiModelRef[] = [];
	private localMessages: AiMessage[] = [
		...this.INIT_LOCAL_MESSAGES
	];
	private activeRequestId?: string;
	copySettings: AiCopySettings = {
		variations: 7,
		layerPurpose: 'Headline',
		minChars: 70,
		maxChars: 160,
		contexts: ['Picnic', 'Restaurant'],
		examples: '',
		exclude: ''
	};
	mediaSettings: AiMediaSettings = {
		results: 4,
		styleType: 'Auto',
		steps: 30,
		seed: 'random',
		negativePrompt: '',
		faceEnhancement: false,
		aspectRatio: '1:1',
		// Video-specific defaults
		mediaType: 'image',
		durationSeconds: 8,
		resolution: '1080p',
		personGeneration: 'allow_adult'
	};

	/** @deprecated Use mediaSettings instead */
	get imageSettings(): AiMediaSettings {
		return this.mediaSettings;
	}
	set imageSettings(value: AiMediaSettings) {
		this.mediaSettings = value;
	}
	brandKit: AiBrandKitSettings = {
		tone: 'Professional',
		includeKeywords: '',
		excludeKeywords: '',
		purpose: ''
	};

	private destroy$ = new Subject<void>();

	constructor(
		private dialog: MatDialog,
		public readonly elementRef: ElementRef,
		@Optional() @Inject(AI_ADAPTER) private adapter?: AiAdapter
		)
	{}

	ngOnChanges(changes: SimpleChanges) {
		// Update settings when config input changes
		if (changes['config'] && this.config) {
			console.log('AiConversationShell config changed:', this.config);

			if (this.config?.copy) {
				this.copySettings = {...this.copySettings, ...this.config.copy};
			}
			// Support both new 'media' and deprecated 'image' config properties
			if (this.config?.media) {
				this.mediaSettings = {...this.mediaSettings, ...this.config.media};
				console.log('Updated mediaSettings:', this.mediaSettings);
			} else if (this.config?.image) {
				this.mediaSettings = {...this.mediaSettings, ...this.config.image};
				console.log('Updated mediaSettings from deprecated image config:', this.mediaSettings);
			}
			if (this.config?.brandKit) {
				this.brandKit = {...this.brandKit, ...this.config.brandKit};
			}
			if (this.config?.defaultModel) {
				this.currentModel = this.config.defaultModel;
			}
			if (this.config?.availableModels && this.config.availableModels.length > 0) {
				this.availableModels = this.config.availableModels;
      }

			if (changes['config'].previousValue?.mode !== this.config.mode) {
				// Mode changed, reset local messages
				this.localMessages = [
					...this.INIT_LOCAL_MESSAGES
				];

				// Also reset models
				this.refreshModels();
			}
		}
	}

	ngOnInit() {
		this.currentModel = this.config?.defaultModel;
		if (this.config?.copy) {
			this.copySettings = {...this.copySettings, ...this.config.copy};
		}
		// Support both new 'media' and deprecated 'image' config properties
		if (this.config?.media) {
			this.mediaSettings = {...this.mediaSettings, ...this.config.media};
		} else if (this.config?.image) {
			this.mediaSettings = {...this.mediaSettings, ...this.config.image};
		}
		if (this.config?.brandKit) {
			this.brandKit = {...this.brandKit, ...this.config.brandKit};
		}

		// Use config.availableModels if provided, otherwise load from adapter
		if (this.config?.availableModels && this.config.availableModels.length > 0) {
			this.availableModels = this.config.availableModels;
		} else if (this.adapter?.models) {
			// Load models from adapter if available
		  this.refreshModels();
    }

	}

	refreshModels() {
		// Load models from adapter if available
		if (this.adapter?.models) {
			this.adapter.models().pipe(takeUntil(this.destroy$)).subscribe(models => {
				const mode = this.config?.mode;
				const need = mode === 'video' ? 'video' : (mode === 'image' || mode === 'image-edit' ? 'image' : 'text');
				this.availableModels = (models || []).filter(m => (m.capabilities || []).includes(need as any));
				// Fallback to first model if current is not present
				if (!this.currentModel || !this.availableModels.find(m => m.id === this.currentModel.id)) {
					this.currentModel = this.availableModels[0] || this.currentModel;
					if (this.currentModel) {
						this.modelChanged.emit(this.currentModel);
					}
				}
			});
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	handleSuggestionClick(s: { label: string; value: string }) {
		// Immediately send suggestion value without inserting into textarea
		this.sendPrompt(s?.value || '');
	}

	handleSend() {
		const text = this.prompt?.trim();
		if (!text) return;
		this.sendPrompt(text);
		this.prompt = '';
	}

	private sendPrompt(text: string) {
		if (!text?.trim()) return;
		if (this.useAdapter && this.adapter) {
			this.isLoading = true;
			const requestId = 'req-' + Date.now();
			this.activeRequestId = requestId;
			// push user message locally
			this.localMessages = [...this.localMessages, {
				id: requestId + '-u',
				role: 'user',
				type: 'text',
				content: text
			}];
			// start streaming assistant message placeholder
			this.localMessages = [...this.localMessages, {
				id: requestId + '-a',
				role: 'assistant',
				type: 'text',
				content: ''
			}];
			const sub = this.adapter.send({
				requestId,
				prompt: text,
				model: this.currentModel,
				mode: this.config?.mode,
				params: {copy: this.copySettings, media: this.mediaSettings, image: this.mediaSettings, brand: this.brandKit}
			}).pipe(takeUntil(this.destroy$)).subscribe({
				next: evt => {
					if (this.isLoading) this.isLoading = false;
					if ((evt as any).chunk !== undefined) {
						// append chunk to last assistant message
						const last = this.localMessages[this.localMessages.length - 1];

						if (last && last.role === 'assistant' && last.type === 'text') {
							last.content = (last.content || '') + (evt as any).chunk;
						}

					} else {
						// replace the placeholder assistant message with final message
						this.localMessages = this.localMessages.slice(0, -1).concat(evt as AiMessage);
					}
				},
				error: err => {
					if (this.isLoading) this.isLoading = false;
					// replace the placeholder assistant message with error message
					this.localMessages = this.localMessages.slice(0, -1).concat({
						id: requestId + '-a-err',
						role: 'assistant',
						type: 'error',
						content: $localize`:Error message@@aiConversationShellErrorMessage:An error occurred while processing your request. Please try again.`
					});
				}
			});
		} else {
			// parent-controlled mode
			this.sendMessage.emit({prompt: text, config: this.config!});
		}
	}

	onEnter(event: KeyboardEvent) {
		if (event.shiftKey) return; // allow newline
		event.preventDefault();
		this.handleSend();
	}

	handleModelChange(model: AiModelRef) {
		if (!this.config?.defaultModel) return;
		this.currentModel = {...this.config.defaultModel, ...model};
		this.modelChanged.emit(this.currentModel);
	}

	removeTag(tag: AiContextTag) {
		if (!this.config?.contextTags) return;
		if (!tag.removable) return;
		this.config.contextTags = this.config.contextTags.filter(t => t !== tag);
	}

	get displayMessages(): AiMessage[] {
		return (this.messages && this.messages.length) ? this.messages : this.localMessages;
	}

	syncCopyConfig() {
		if (this.config) this.config.copy = {...this.copySettings};
	}

	addContext(input: HTMLInputElement, event: KeyboardEvent) {
		event.preventDefault();
		const v = (input.value || '').trim();
		if (!v) return;
		if (!this.copySettings.contexts.includes(v)) {
			this.copySettings.contexts.push(v);
			this.syncCopyConfig();
		}
		input.value = '';
	}

	syncMediaConfig() {
		if (this.config) {
			this.config.media = {...this.mediaSettings};
			this.config.image = {...this.mediaSettings}; // Keep deprecated property in sync
		}
	}

	/** @deprecated Use syncMediaConfig instead */
	syncImageConfig() {
		this.syncMediaConfig();
	}

	setSeed(value: string) {
		const v = (value || '').trim();
		if (!v) {
			this.mediaSettings.seed = 'random';
		} else {
			const n = Number(v);
			this.mediaSettings.seed = isNaN(n) ? 'random' : n;
		}
		this.syncMediaConfig();
	}

	onResultAction(evt: any) {
		this.resultAction.emit(evt);
	}

	syncBrandKitConfig() {
		if (this.config) this.config.brandKit = {...this.brandKit};
	}

	get hasStarted(): boolean {
		const msgs = this.displayMessages || [];
		return msgs.some(m => m.role === 'user');
	}

	/**
	 * Handle add reference images button click
	 * @param type Optional reference type for video mode
	 */
	onAddReferenceImages(type?: VideoReferenceType): void {
		this.addReferenceImages.emit(type);
	}

	/**
	 * Check if a reference image of the given type already exists
	 */
	hasReferenceOfType(type: VideoReferenceType): boolean {
		return this.mediaSettings.referenceImages?.some(r => r.referenceType === type) || false;
	}

	/**
	 * Get display label for reference type
	 */
	getReferenceTypeLabel(type: VideoReferenceType): string {
		const labels: Record<VideoReferenceType, string> = {
			'style': 'Style',
			'subject': 'Subject',
			'first_frame': 'First',
			'last_frame': 'Last'
		};
		return labels[type] || type;
	}

	/**
	 * Remove a reference image by index
	 */
	onRemoveReferenceImage(index: number): void {
		if (!this.mediaSettings.referenceImages) return;
		this.mediaSettings.referenceImages.splice(index, 1);
		this.syncMediaConfig();
		this.removeReferenceImage.emit(index);
	}

	/**
	 * Check if max reference images reached
	 */
	isMaxReferenceImagesReached(): boolean {
		const max = this.mediaSettings.maxReferenceImages || 14;
		const current = this.mediaSettings.referenceImages?.length || 0;
		return current >= max;
	}

	onRemoveBradKitOption(option: keyof AiBrandKitSettings) {
		if (this.brandKit && option in this.brandKit) {
			(this.brandKit as any)[option] = '';
			this.syncBrandKitConfig();
		}
	}
	onRemoveCopyOption(option: keyof AiCopySettings) {
		if (this.copySettings && option in this.copySettings) {
			if (option === 'contexts') {
				this.copySettings.contexts = [];
			} else {
				(this.copySettings as any)[option] = '';
			}
			this.syncCopyConfig();
		}
	}
}
